import { Alignment, Borders, Cell, FillPattern, Font, Row, Workbook, Worksheet } from 'exceljs';
import { get, set } from 'lodash';
import { saveAs } from 'file-saver';
import moment from 'moment';
import { displayPrizeType, formatDateToDisplay, getDisplayQuarterByDate } from '@/helpers';
import { RewardItemDisplayDTO, RewardPeriodDTO, RewardRequestDTO } from '@/models';
import { NA, STYLES } from './project-exporting-config';
import { sortArrByRewardRequestStatus, sortArrByTextField } from '@/services/table-sorting';
import { REWARD_REQUEST_STATUSES } from '@/constants';
import { addTableHeaderRow } from './project-exporting-helpers';

type Style = {
  fill?: FillPattern;
  border?: Partial<Borders>;
  font?: Partial<Font>;
  alignment?: Partial<Alignment>;
};

type callbackFn = (cell: Cell, index: number) => void;
const EMPTY = '';

export default class ExportRewardPeriodDetail {
  private workbook = new Workbook();
  private ioWS: Worksheet;
  private riWS: Worksheet;
  private rrWS: Worksheet;
  private readonly rewardPeriod: RewardPeriodDTO;
  private readonly rewardItems: RewardItemDisplayDTO[];
  private readonly rewardRequests: RewardRequestDTO[];
  private columnWidths = [8, 30, 30, 18, 18, 18, 18, 18, 18, 18, 18, 18];

  constructor(rewardPeriod: RewardPeriodDTO, rewardItems: RewardItemDisplayDTO[], rewardRequests: RewardRequestDTO[]) {
    this.rewardPeriod = rewardPeriod;
    this.rewardItems = rewardItems;
    this.rewardRequests = rewardRequests;
  }

  async generateXLSXFile(): Promise<void> {
    this.ioWS = this.workbook.addWorksheet('General Information');

    const rewardPeriodInformation = this.buildRewardPeriodInformation(this.rewardPeriod);
    this.addGeneralInformationToWorkSheet('Information', rewardPeriodInformation, 1);
    this.addEmptyRow(this.ioWS);
    this.addEmptyRow(this.ioWS);

    const rewardPeriodOverall = this.buildRewardPeriodOverall(this.rewardItems, this.rewardRequests);
    this.addGeneralInformationToWorkSheet('Overall', rewardPeriodOverall, 11);

    this.riWS = this.workbook.addWorksheet('Prize Items');
    this.addRewardItemsToWorksheet(this.rewardItems, 1);

    this.rrWS = this.workbook.addWorksheet('Prize Requests');
    this.addRewardRequestsToWorksheet(this.rewardRequests, 1);

    this.setColumnWidth();
    try {
      const buf = await this.workbook.xlsx.writeBuffer();
      const blob = new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      saveAs(blob, this.exportFileName);
    } catch (error) {
      console.log(error);
    }
  }

  addRow(ws: Worksheet, data: any[], style?: Style, callback?: callbackFn): Row {
    const row = ws.addRow(data);
    let maxHeight = 20;

    row.eachCell((cell, index) => {
      if (cell.value !== EMPTY) {
        if (typeof cell.value === 'number') {
          cell.alignment = STYLES.middleRightWrappedAlignment;
        } else {
          if (cell.value) {
            const values = cell.value.toString().split(/[\n\r]+/);

            for (const value of values) {
              let height = value.length;

              if (cell.font && cell.font.bold) {
                height *= 1.08;
              }
              maxHeight = Math.max(maxHeight, height);
            }
          }

          cell.alignment = STYLES.middleLeftWrappedAlignment;
        }
        if (cell.value === NA) {
          cell.font = STYLES.naFont;
          cell.alignment = STYLES.middleRightWrappedAlignment;
        }
        this.addStyleToCell(cell, style);
        if (callback) {
          callback(cell, index);
        }
      }
    });
    row.height = maxHeight;
    return row;
  }

  addGeneralInformationToWorkSheet(title: string, rows: any[], rowPosition: number): void {
    const ws = this.ioWS;
    this.addTitleRow(ws, title, rows[0].length, rowPosition);
    this.addEmptyRow(ws);

    rows.forEach((rowData, rowIndex) => {
      if (rowIndex === 0) {
        this.addRow(ws, rowData, { border: STYLES.primaryTealBorderTop, fill: STYLES.lightTealFill }, (cell, index) => {
          if (index === 2) {
            cell.font = STYLES.boldFont;
          }
        });
        return;
      } else if (rowIndex === rows.length - 1) {
        const style =
          rowIndex % 2 === 0
            ? { border: STYLES.primaryTealBorderBottom, fill: STYLES.lightTealFill }
            : { border: STYLES.primaryTealBorderBottom };
        this.addRow(ws, rowData, style, (cell, index) => {
          if (index === 2) {
            cell.font = STYLES.boldFont;
          }
        });
        return;
      }
      this.interleaveRowStyle(ws, rowData, rowIndex, (cell, index) => {
        if (index === 2) {
          cell.font = STYLES.boldFont;
        }
      });
    });
  }

  addRewardItemsToWorksheet(rewardItems: RewardItemDisplayDTO[], rowPosition: number): void {
    const ws = this.riWS;
    const header = [
      null,
      { title: 'Name' },
      { title: 'Description' },
      { title: 'Type' },
      { title: 'Estimated Price' },
      { title: 'Cost (pollens)' },
      { title: 'Quantity' },
      { title: 'Redeemed' }
    ];
    this.addTitleRow(ws, `Prize items`, header.length, rowPosition);
    this.addEmptyRow(ws);
    addTableHeaderRow(ws, header);
    const itemRows = sortArrByTextField(rewardItems, 'name', false).map(item => {
      return this.buildRewardItemInformation(item);
    });

    this.addDataRows(ws, itemRows);
  }

  getItemNameOfRewardRequest(rewardRequest: RewardRequestDTO): string {
    return rewardRequest.rewardItem.prize.name || NA;
  }

  addRewardRequestsToWorksheet(rewardRequests: RewardRequestDTO[], rowPosition: number): void {
    const ws = this.rrWS;
    const header = [
      null,
      { title: 'User' },
      { title: 'Item' },
      { title: 'Category', style: { fill: STYLES.lightOrangeFill } },
      { title: 'Cost' },
      { title: 'Estimated Price' },
      { title: 'Actual Price' },
      { title: 'Created At' },
      { title: 'Resolved At' },
      { title: 'Status' },
      { title: 'Reason' },
      { title: 'Period', style: { fill: STYLES.lightOrangeFill } }
    ];
    this.addTitleRow(ws, `Prize requests`, header.length, rowPosition);
    this.addEmptyRow(ws);
    addTableHeaderRow(ws, header);
    const requestRows = sortArrByRewardRequestStatus(rewardRequests.slice(), 'status', false).map(request => {
      return this.buildRewardRequestInformation(request);
    });

    this.addDataRows(ws, requestRows);
  }

  addStyleToCell(cell: Cell, style: Style = {}): void {
    Object.keys(style).map(key => set(cell, key, get(style, key)));
  }

  addTitleRow(ws: Worksheet, title: string, mergeLength: number, rowPosition: number): void {
    const row = ws.addRow([title, ...Array(mergeLength - 2).fill('')]);
    this.mergeCells(ws, row, 1, mergeLength);
    const address = `${row.getCell(1).address}`;
    ws.getCell(address).font = STYLES.h1Font;
    ws.getRow(rowPosition).height = 45;
  }

  addEmptyRow(ws: Worksheet): void {
    ws.addRow([]);
  }

  addDataRows(ws: Worksheet, rows: any[]): void {
    rows.forEach((row, index) => {
      if (row) {
        if (index === rows.length - 1) {
          const style =
            index % 2 === 0
              ? { border: STYLES.primaryTealBorderBottom, fill: STYLES.lightTealFill }
              : { border: STYLES.primaryTealBorderBottom };
          this.addRow(ws, row, style);
          return;
        }
        this.interleaveRowStyle(ws, row, index);
      }
    });
  }

  buildRewardPeriodInformation(rewardPeriod: RewardPeriodDTO): any[] {
    const { name, description, createdBy, createdAt, startDate, endDate } = rewardPeriod;
    const result = [
      ['Name', name],
      ['Description', description],
      ['Created by', `${createdBy.fullName} - ${createdBy.code}`],
      ['Created at', formatDateToDisplay(createdAt)],
      ['Start date', formatDateToDisplay(startDate)],
      ['End date', formatDateToDisplay(endDate)]
    ];
    return result.map(row => [...Array(1).fill(EMPTY), ...row]);
  }

  buildRewardPeriodOverall(rewardItems: RewardItemDisplayDTO[], rewardRequests: RewardRequestDTO[]): any[] {
    const approvedRequests = rewardRequests.filter(
      request => request.status === REWARD_REQUEST_STATUSES.APPROVED.label
    );
    const requestItems = approvedRequests.length;
    const dropsSpent = approvedRequests.reduce((drops, request) => drops + request.rewardItem.cost, 0);
    const estimatedFundSpent = approvedRequests.reduce(
      (drops, request) => drops + request.rewardItem.estimatedPrice,
      0
    );
    const actualFundSpent = approvedRequests.reduce((drops, request) => drops + (request.actualPrice ?? 0), 0);
    const totalItems = rewardItems.reduce((nItems, item) => nItems + item.quantity, 0);
    const totalDrops = rewardItems.reduce((drops, item) => drops + item.cost * item.quantity, 0);
    const totalFund = rewardItems.reduce((drops, item) => drops + item.estimatedPrice * item.quantity, 0);
    const result = [
      ['Request item(s)', requestItems],
      ['Pollens spent', dropsSpent],
      ['Estimated fund spend', estimatedFundSpent],
      ['Actual fund spent', actualFundSpent],
      ['Total item(s)', totalItems],
      ['Total pollens', totalDrops],
      ['Total fund', totalFund]
    ];
    return result.map(row => [...Array(1).fill(EMPTY), ...row]);
  }

  buildRewardItemInformation(rewardItem: RewardItemDisplayDTO): any[] {
    const { estimatedPrice, cost, description, name, redeemCount, quantity, type } = rewardItem;
    return [null, name, description ?? NA, displayPrizeType(type), estimatedPrice, cost, quantity, redeemCount];
  }

  buildRewardRequestInformation(rewardRequest: RewardRequestDTO): any[] {
    const { user, status, reason, rewardItem, createdAt, updatedAt, actualPrice } = rewardRequest;
    const rewardItemName = this.getItemNameOfRewardRequest(rewardRequest);
    const category = displayPrizeType(rewardItem?.prize?.type);
    return [
      null,
      `${user.fullName} - ${user.code}`,
      rewardItemName,
      category,
      rewardItem.cost,
      rewardItem.estimatedPrice,
      actualPrice,
      formatDateToDisplay(createdAt || ''),
      formatDateToDisplay(updatedAt || ''),
      status,
      reason ?? NA,
      getDisplayQuarterByDate(createdAt || '')
    ];
  }

  mergeCells(ws: Worksheet, row: Row, from: number, to: number): void {
    ws.mergeCells(`${row.getCell(from).address}:${row.getCell(to).address}`);
  }

  interleaveRowStyle(ws: Worksheet, data: any[], index: number, callbackFn?: callbackFn): Row {
    return index % 2 === 0
      ? this.addRow(ws, data, { fill: STYLES.lightTealFill }, callbackFn)
      : this.addRow(ws, data, {}, callbackFn);
  }

  setColumnWidth(): void {
    this.ioWS.columns.forEach((column, index) => {
      column.width = this.columnWidths[index];
    });
    this.riWS.columns.forEach((column, index) => {
      column.width = this.columnWidths[index];
    });
    this.rrWS.columns.forEach((column, index) => {
      column.width = this.columnWidths[index];
    });
  }

  get exportFileName(): string {
    const currentDate = moment(new Date()).format('YYYYMMDD');
    return `HIVE_Reward_${this.rewardPeriod.name}_${currentDate}.xlsx`;
  }
}
